'use client'

import { useState } from 'react'
import { createPortal } from 'react-dom'
import { useRouter } from 'next/navigation'
import { Download, Lock, Check, Trash2, Music, Video, X } from 'lucide-react'
import { usePro } from '@/lib/pro'
import { saveDownload, deleteDownload, useDownloaded } from '@/lib/downloads'
import { showToast, updateToast, dismissToast } from '@/lib/toast'
import type { PlayerTrack } from '@/lib/player-context'

type Props = {
  track: PlayerTrack
  size?: 'sm' | 'md'
  /** Si défini, télécharge DIRECTEMENT ce format (pas de popup MP3/MP4). */
  directKind?: 'audio' | 'video'
  /** Libellé texte à côté de l'icône (bouton « pilule » au lieu d'icône seule). */
  label?: string
}

export default function DownloadButton({ track, size = 'sm', directKind, label }: Props) {
  const { active } = usePro()
  const router = useRouter()
  const downloaded = useDownloaded(track.videoId)
  const [state, setState] = useState<'idle' | 'loading' | 'done' | 'error'>('idle')
  const [progress, setProgress] = useState(0)  // 0 → 1
  const [errMsg, setErrMsg] = useState<string | null>(null)
  const [showPopup, setShowPopup] = useState(false)

  // Clic sur le bouton : si déjà téléchargé → supprime ; sinon → ouvre la popup
  // de choix de format (MP3 / MP4).
  const onClick = async () => {
    if (state === 'loading') return
    if (downloaded) {
      await deleteDownload(track.videoId)
      showToast({ kind: 'info', msg: 'Titre supprimé du stockage local', thumbnail: track.thumbnail })
      return
    }
    // Contexte vidéo : téléchargement direct MP4, pas de popup de choix.
    if (directKind) { doDownload(directKind); return }
    setShowPopup(true)
  }

  /** Téléchargement VIDÉO (MP4) : le serveur ne peut pas récupérer YouTube, donc
   *  il résout juste l'URL (via l'API vidéo RapidAPI) et TON navigateur la
   *  télécharge (IP résidentielle). googlevideo n'a pas de CORS → on tente
   *  fetch→blob (nom propre) puis on retombe sur un lien direct. */
  const doVideoDownload = async () => {
    setState('loading'); setProgress(0); setErrMsg(null)
    const toastId = showToast({ kind: 'progress', msg: `Préparation de "${truncate(track.title, 28)}"`, thumbnail: track.thumbnail, progress: 0.3, duration: -1 })
    try {
      const j = await fetch(`/api/video/mp4url?id=${encodeURIComponent(track.videoId)}`).then(r => r.json()).catch(() => null)
      if (!j?.ok || !j.url) throw new Error(j?.error || 'Vidéo indisponible')
      const safe = String(track.title || track.videoId).replace(/[<>:"/\\|?*\x00-\x1F]/g, '').slice(0, 90).trim() || track.videoId
      const fileName = `${safe}.mp4`
      updateToast(toastId, { progress: 0.6, msg: 'Téléchargement…' })
      let done = false
      try {
        const resp = await fetch(j.url)   // peut échouer (CORS googlevideo)
        if (resp.ok) {
          const blob = await resp.blob()
          if (blob.size > 100000) {
            const url = URL.createObjectURL(blob)
            const a = document.createElement('a'); a.href = url; a.download = fileName
            document.body.appendChild(a); a.click(); document.body.removeChild(a)
            setTimeout(() => URL.revokeObjectURL(url), 2000)
            done = true
          }
        }
      } catch { /* CORS → repli lien direct */ }
      if (!done) {
        const a = document.createElement('a')
        a.href = j.url; a.download = fileName; a.target = '_blank'; a.rel = 'noopener'
        document.body.appendChild(a); a.click(); document.body.removeChild(a)
      }
      dismissToast(toastId)
      showToast({ kind: 'success', msg: `🎬 Vidéo${j.quality ? ` ${j.quality}` : ''} : téléchargement lancé`, thumbnail: track.thumbnail, duration: 3500 })
      setState('done'); setProgress(1); setTimeout(() => { setState('idle'); setProgress(0) }, 2500)
    } catch (e: any) {
      dismissToast(toastId)
      showToast({ kind: 'error', msg: e?.message?.slice(0, 90) || 'Échec du téléchargement vidéo', thumbnail: track.thumbnail, duration: 5000 })
      setState('error'); setTimeout(() => { setState('idle'); setProgress(0) }, 4000)
    }
  }

  /** Lance le téléchargement du format choisi (audio MP3 ou vidéo MP4). */
  const doDownload = async (kind: 'audio' | 'video') => {
    setShowPopup(false)
    if (kind === 'video') { doVideoDownload(); return }
    // À partir d'ici : kind === 'audio' uniquement (la vidéo part via doVideoDownload).
    const isVideo = false
    const ext = 'mp3'
    const mime = 'audio/mpeg'

    setState('loading')
    setProgress(0)
    setErrMsg(null)

    // Toast en cours avec vignette
    const toastId = showToast({
      kind: 'progress',
      msg: `Téléchargement de "${truncate(track.title, 32)}"`,
      thumbnail: track.thumbnail,
      progress: 0,
      duration: -1,  // permanent jusqu'à mise à jour
    })

    // ============== Voie A : Electron desktop (AUDIO uniquement) ==============
    // L'app desktop utilise yt-dlp local + cookies Chrome de l'utilisateur (anti-bot bypass natif).
    const blcDesktop = (typeof window !== 'undefined' ? (window as any).blcDesktop : null)
    if (!isVideo && blcDesktop?.downloadTrack) {
      try {
        updateToast(toastId, { progress: 0.5, msg: `Téléchargement (cookies Chrome locaux)...` })
        const res = await blcDesktop.downloadTrack({
          videoId: track.videoId,
          title:   track.title,
          artist:  track.artist,
        })
        if (!res?.ok) throw new Error(res?.error || 'Échec yt-dlp local')

        dismissToast(toastId)
        showToast({
          kind: 'success',
          msg: `Téléchargé dans Downloads : ${res.fileName}`,
          thumbnail: track.thumbnail,
          duration: 4500,
        })
        // Ouvre le dossier au click sur le toast (optionnel — sinon juste reveal direct)
        try { await blcDesktop.revealFile?.(res.path) } catch {}

        setState('done')
        setProgress(1)
        setTimeout(() => { setState('idle'); setProgress(0) }, 2500)
        return
      } catch (e: any) {
        // Fallback vers le serveur si Electron local échoue
        // eslint-disable-next-line no-console
        console.warn('[BLC] Electron download failed, fallback serveur:', e?.message)
        updateToast(toastId, { msg: 'Tentative côté serveur…' })
      }
    }

    // ============== Voie B : Serveur (navigateur / APK) ==============
    try {
      // 1) On demande au serveur le LIEN MP3 (via l'API tierce). Le serveur ne
      //    proxy pas le fichier (son IP est blacklistée par le CDN) → il nous
      //    renvoie l'URL et NOTRE navigateur (IP résidentielle) la télécharge.
      const r = await fetch(`/api/youtube/download?v=${encodeURIComponent(track.videoId)}&kind=${kind}`)
      const ct = r.headers.get('Content-Type') || ''

      let fetchResp: Response
      let fileName: string

      if (ct.includes('application/json')) {
        const j = await r.json().catch(() => null)
        if (!j || !j.ok || !j.url) {
          const msg = (j && j.error) || `HTTP ${r.status}`
          setErrMsg(msg)
          throw new Error(msg)
        }
        const safe = String(j.title || track.title || track.videoId)
          .replace(/[<>:"/\\|?*\x00-\x1F]/g, '').slice(0, 90).trim()
        fileName = `${safe || track.videoId}.${ext}`
        updateToast(toastId, { progress: 0.05, msg: `Téléchargement de "${truncate(track.title, 28)}"` })
        // Téléchargement DIRECT depuis le CDN (CORS *), connexion résidentielle.
        fetchResp = await fetch(j.url)
        if (!fetchResp.ok) throw new Error(`CDN HTTP ${fetchResp.status}`)
      } else {
        // Fallback : le serveur a renvoyé les octets directement (yt-dlp).
        if (!r.ok) {
          const txt = await r.text().catch(() => '')
          setErrMsg(txt || `HTTP ${r.status}`)
          throw new Error(txt || `HTTP ${r.status}`)
        }
        fetchResp = r
        const cd = r.headers.get('Content-Disposition') || ''
        const m = cd.match(/filename="([^"]+)"/)
        fileName = m ? decodeURIComponent(m[1]) : `${track.title || track.videoId}.${ext}`
      }

      const total = Number(fetchResp.headers.get('Content-Length') || '0')
      const reader = fetchResp.body?.getReader()
      let received = 0
      const chunks: Uint8Array[] = []

      if (reader) {
        while (true) {
          const { done, value } = await reader.read()
          if (done) break
          if (value) {
            chunks.push(value)
            received += value.byteLength
            const pct = total > 0 ? received / total : 0
            setProgress(pct)
            updateToast(toastId, { progress: pct || 0.5 })
          }
        }
      } else {
        const buf = await fetchResp.arrayBuffer()
        chunks.push(new Uint8Array(buf))
        received = buf.byteLength
        setProgress(1)
        updateToast(toastId, { progress: 1 })
      }

      const blob = new Blob(chunks as BlobPart[], { type: mime })
      if (blob.size < 10_000) throw new Error('Fichier vide ou indisponible')

      // 1) Pour l'AUDIO : on stocke dans IndexedDB (bibliothèque hors-ligne) +
      //    auto-ingest reco. Pour la VIDÉO : on ne stocke PAS en bibliothèque
      //    (c'est un lecteur audio), on déclenche juste le download système.
      if (!isVideo) {
        await saveDownload({
          videoId: track.videoId,
          title: track.title,
          artist: track.artist,
          thumbnail: track.thumbnail,
          fileName,
          size: blob.size,
          savedAt: Date.now(),
          blob,
        })
        // Auto-ingest reco Shazam (fire-and-forget)
        try {
          const fd2 = new FormData()
          fd2.append('audio', blob, fileName)
          fd2.append('title', track.title || '')
          fd2.append('artist', track.artist || '')
          fetch('/api/recognize/ingest', { method: 'POST', body: fd2 })
            .then(r => r.json())
            .then(j => { if (j?.ok) console.log('[BLC] auto-ingest OK', j.hashes, 'hashes') })
            .catch(() => {})
        } catch {}
      }

      // 2) DESTINATION FICHIER :
      //    - APK Android : on écrit dans un DOSSIER PERMANENT « Musique/BLC Music »
      //      (visible dans le gestionnaire de fichiers + les autres lecteurs).
      //    - Web / desktop : téléchargement navigateur classique → dossier
      //      Téléchargements système.
      const android = (typeof window !== 'undefined') ? (window as any).AndroidAudio : null
      let savedToFolder = false
      if (android?.saveMusic && !isVideo) {
        try {
          updateToast(toastId, { progress: 1, msg: 'Enregistrement dans le dossier…' })
          const base64 = await blobToBase64(blob)
          savedToFolder = !!android.saveMusic(fileName, base64)
        } catch { savedToFolder = false }
      }
      if (!savedToFolder) {
        try {
          const dlUrl = URL.createObjectURL(blob)
          const a = document.createElement('a')
          a.href = dlUrl
          a.download = fileName
          document.body.appendChild(a)
          a.click()
          document.body.removeChild(a)
          setTimeout(() => URL.revokeObjectURL(dlUrl), 1500)
        } catch {/* */}
      }

      // Remplace le toast progress par un toast success
      dismissToast(toastId)
      showToast({
        kind: 'success',
        msg: savedToFolder
          ? `🎵 Enregistré dans Musique/BLC Music : ${fileName.slice(0, 30)}`
          : `${isVideo ? '🎬 Vidéo' : '🎵 Audio'} téléchargé : ${fileName.slice(0, 36)}`,
        thumbnail: track.thumbnail,
        duration: 3500,
      })

      setState('done')
      setProgress(1)
      setTimeout(() => { setState('idle'); setProgress(0) }, 2500)
    } catch (e: any) {
      console.error('download failed', e)
      dismissToast(toastId)
      showToast({
        kind: 'error',
        msg: e?.message?.slice(0, 80) || 'Échec du téléchargement',
        thumbnail: track.thumbnail,
        duration: 5000,
      })
      setState('error')
      setTimeout(() => { setState('idle'); setProgress(0) }, 4000)
    }
  }

  const px = size === 'sm' ? 'p-2' : 'p-2.5'
  const ic = size === 'sm' ? 14 : 16
  const ringSize = size === 'sm' ? 28 : 32

  let cls = 'rounded-md transition'
  let icon: React.ReactNode = <Download size={ic} />
  let title = 'Télécharger en hors ligne'

  if (!active) {
    cls += ' text-white/55 hover:text-a4'
    icon = <Lock size={ic} />
    title = 'Réservé à BLC Pro — clique pour découvrir'
  } else if (state === 'loading') {
    return (
      <button title={`Téléchargement... ${Math.round(progress * 100)}%`}
        className={label
          ? 'inline-flex items-center gap-2 rounded-full bg-white/[0.07] border border-white/10 px-3.5 py-2 text-sm font-semibold text-white'
          : `relative ${px} rounded-md text-a4`}>
        <CircularProgress value={progress * 100} size={label ? 18 : ringSize} />
        {label && <span>{Math.round(progress * 100)}%</span>}
      </button>
    )
  } else if (downloaded) {
    cls += ' text-a4 hover:text-rose-400'
    icon = <Trash2 size={ic} />
    title = 'Disponible hors ligne — clic pour supprimer'
  } else if (state === 'done') {
    cls += ' text-emerald-400'
    icon = <Check size={ic} />
    title = 'Sauvegardé hors ligne !'
  } else if (state === 'error') {
    cls += ' text-rose-400'
    title = errMsg || 'Échec du téléchargement'
  } else {
    cls += ' text-white/65 hover:text-a4'
  }

  return (
    <>
      {label ? (
        <button onClick={onClick} title={title}
          className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-2 text-sm font-semibold transition ${
            !active ? 'bg-white/[0.07] border border-white/10 text-white/60 hover:text-white'
            : state === 'done' ? 'bg-emerald-600/20 text-emerald-300'
            : downloaded ? 'bg-a2/15 border border-a2/40 text-a5 hover:text-rose-300'
            : 'bg-white/[0.07] border border-white/10 text-white hover:bg-white/[0.12]'}`}>
          {icon}<span>{state === 'done' ? 'Téléchargé' : downloaded ? 'Supprimer' : label}</span>
        </button>
      ) : (
        <button onClick={onClick} title={title} className={`${cls} ${px}`}>
          {icon}
        </button>
      )}

      {/* Popup choix de format MP3 / MP4 */}
      {showPopup && typeof document !== 'undefined' && createPortal(
        <div
          className="fixed inset-0 z-[120] flex items-center justify-center p-4"
          style={{ background: 'rgba(7,4,24,0.88)', backdropFilter: 'blur(8px)' }}
          onClick={() => setShowPopup(false)}
        >
          <div
            onClick={e => e.stopPropagation()}
            className="w-full max-w-xs rounded-3xl bg-spanel border border-white/[0.09] p-5 shadow-[0_24px_60px_rgba(0,0,0,.6),0_0_40px_rgb(var(--a2)/.25)] relative"
            style={{ animation: 'blcPopIn 0.22s ease-out' }}
          >
            <button onClick={() => setShowPopup(false)}
              className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/[0.07] hover:bg-white/[0.14] flex items-center justify-center text-white/60 hover:text-white">
              <X size={14} />
            </button>
            <div className="text-center mb-1">
              <div className="text-[10px] tracking-[0.24em] text-a4 font-semibold">TÉLÉCHARGER CETTE MUSIQUE ?</div>
              <div className="text-sm font-semibold text-white truncate mt-1 px-4">{track.title}</div>
              <div className="text-[10px] text-white/45 mt-1">Choisis le format pour confirmer 👇</div>
            </div>
            <div className="grid grid-cols-2 gap-3 mt-4">
              {/* MP3 audio */}
              <button onClick={() => doDownload('audio')}
                className="flex flex-col items-center gap-2 p-4 rounded-2xl bg-a2/15 border border-a2/45 hover:border-a3 hover:bg-a2/25 active:scale-95 transition">
                <span className="w-12 h-12 rounded-full bg-gradient-to-br from-a2 to-a1 shadow-[0_6px_18px_rgb(var(--a2)/.5)] flex items-center justify-center text-white">
                  <Music size={22} />
                </span>
                <span className="text-sm font-bold text-white">MP3</span>
                <span className="text-[10px] text-white/55">Audio · 320 kbps</span>
              </button>
              {/* MP4 vidéo */}
              <button onClick={() => doDownload('video')}
                className="flex flex-col items-center gap-2 p-4 rounded-2xl bg-white/[0.05] border border-white/10 hover:border-a3/70 hover:bg-white/[0.08] active:scale-95 transition">
                <span className="w-12 h-12 rounded-full bg-gradient-to-br from-[#3B3FD0] to-adeep flex items-center justify-center text-white">
                  <Video size={22} />
                </span>
                <span className="text-sm font-bold text-white">MP4</span>
                <span className="text-[10px] text-white/55">Vidéo HD</span>
              </button>
            </div>
            <div className="text-[10px] text-white/40 text-center mt-3">
              📁 Le MP3 va dans le dossier <b className="text-white/60">Musique/BLC Music</b> de ton téléphone (et dans ta bibliothèque hors-ligne).
            </div>
          </div>
          <style>{`@keyframes blcPopIn { from { opacity:0; transform: scale(0.92) translateY(8px); } to { opacity:1; transform: scale(1) translateY(0); } }`}</style>
        </div>,
        document.body
      )}
    </>
  )
}

/** Roue de progression circulaire (0–100). */
function CircularProgress({ value, size = 28 }: { value: number; size?: number }) {
  const v = Math.max(0, Math.min(100, value))
  const stroke = 3.2
  return (
    <svg width={size} height={size} viewBox="0 0 36 36" style={{ transform: 'rotate(-90deg)' }}>
      <circle cx="18" cy="18" r="15.9155"
        fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth={stroke} />
      <circle cx="18" cy="18" r="15.9155"
        fill="none" strokeWidth={stroke} strokeLinecap="round"
        pathLength={100}
        strokeDasharray={`${v} ${100 - v}`}
        style={{ stroke: 'rgb(var(--a3))', transition: 'stroke-dasharray .25s' }} />
      {/* Pourcentage centré */}
      <text x="18" y="18" textAnchor="middle" dominantBaseline="central"
        fill="#fff" fontSize="11" fontWeight="700"
        style={{ transform: 'rotate(90deg)', transformOrigin: 'center' }}>
        {Math.round(v)}
      </text>
    </svg>
  )
}

function truncate(s: string, n: number) { return s.length > n ? s.slice(0, n - 1) + '…' : s }

/** Convertit un Blob en base64 (sans le préfixe data:) — pour le pont Android. */
function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const fr = new FileReader()
    fr.onload = () => resolve(String(fr.result).split(',')[1] || '')
    fr.onerror = () => reject(new Error('read error'))
    fr.readAsDataURL(blob)
  })
}
